import { useQuery } from "@tanstack/react-query";
import { Link, useSearch } from "@tanstack/react-router";
import type { ReactElement } from "react";
import { ContentHeader, QueryStatus } from "@cx/ui";
import type { CheckoutPickingList } from "@cx/contracts";
import { MAX_DOC_STOCK_LINES, MAX_PICKING_ORDERS } from "@cx/domain";
import { useSession } from "../../session";
import { qty } from "./labels";

/** Relatório de separação (picking list): produtos consolidados dos pedidos, na ordem da localização. */
export function PickingListPage(): ReactElement {
  const { companyId, api } = useSession();
  const { ids } = useSearch({ from: "/vendas/checkout/separacao" });
  const list = useQuery({
    queryKey: ["checkout", companyId, "picking", ids ?? ""],
    queryFn: () =>
      api<CheckoutPickingList>(`/checkout/picking-list?${new URLSearchParams(ids ? { ids } : {})}`),
  });
  const d = list.data;
  return (
    <>
      <ContentHeader
        title="Relatório de separação"
        breadcrumb={["Vendas", "Checkout de pedidos", "Relatório de separação"]}
        actions={
          <div className="d-flex gap-2 d-print-none">
            <Link to="/vendas/checkout" className="btn btn-outline-secondary">
              Voltar à fila
            </Link>
            <button type="button" className="btn btn-primary" onClick={() => window.print()} disabled={!d}>
              Imprimir
            </button>
          </div>
        }
      />
      <div className="box card">
        <QueryStatus loading={list.isLoading} error={list.error} />
        {d && (
          <>
            <p className="small text-muted p-3 mb-0">
              {d.orders.length === 0
                ? "Nenhum pedido para separar."
                : `Pedidos: ${d.orders.map((o) => `nº ${o.number}`).join(", ")}.`}
              {d.truncated &&
                ` A fila tem mais pedidos: o relatório traz os ${MAX_PICKING_ORDERS} primeiros. Selecione os pedidos na fila para os demais.`}
              {d.skipped.length > 0 &&
                ` Fora do relatório (mais de ${MAX_DOC_STOCK_LINES.toLocaleString("pt-BR")} itens a conferir, contando os componentes dos kits): ${d.skipped.map((n) => `nº ${n}`).join(", ")}.`}
            </p>
            {d.lines.length > 0 && (
              <div className="table-responsive">
                <table className="table align-middle mb-0" aria-label="Produtos a separar">
                  <thead>
                    <tr>
                      <th scope="col">Localização</th>
                      <th scope="col">Produto</th>
                      <th scope="col">SKU</th>
                      <th scope="col">GTIN</th>
                      <th scope="col" className="text-end">
                        Quantidade
                      </th>
                      <th scope="col">Pedidos</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.lines.map((l) => (
                      <tr key={l.productId}>
                        <td>{l.location ?? "—"}</td>
                        <td>{l.name}</td>
                        <td>{l.sku ?? "—"}</td>
                        <td>{l.gtin ?? "—"}</td>
                        <td className="text-end">
                          {qty(l.qtyQ4)} {l.unit}
                        </td>
                        <td className="small">{l.orderNumbers.join(", ")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}
